import { apiClient } from '@/lib/api';
import { ENDPOINTS } from '@/lib/api/endpoints';
import {
  Currency,
  CreateCurrencyData,
  UpdateCurrencyData,
  LOB,
  CreateLOBData,
  UpdateLOBData,
  TaxConfig,
  CreateTaxConfigData,
  UpdateTaxConfigData,
  RateCard,
  CreateRateCardData,
  UpdateRateCardData,
  Country,
  CreateCountryData,
  UpdateCountryData,
  QuoteType,
  CreateQuoteTypeData,
  UpdateQuoteTypeData,
  ImportResult,
} from '../types/master-data.types';

/**
 * Servicio para datos maestros
 */
class MasterDataService {
  // ============ Currencies ============
  async getCurrencies(): Promise<Currency[]> {
    return apiClient.get<Currency[]>(ENDPOINTS.CURRENCIES);
  }

  async getCurrency(id: string): Promise<Currency> {
    return apiClient.get<Currency>(`${ENDPOINTS.CURRENCIES}/${id}`);
  }

  async createCurrency(data: CreateCurrencyData): Promise<Currency> {
    return apiClient.post<Currency>(ENDPOINTS.CURRENCIES, data);
  }

  async updateCurrency(id: string, data: UpdateCurrencyData): Promise<Currency> {
    return apiClient.put<Currency>(`${ENDPOINTS.CURRENCIES}/${id}`, data);
  }

  async deleteCurrency(id: string): Promise<void> {
    return apiClient.delete<void>(`${ENDPOINTS.CURRENCIES}/${id}`);
  }

  async syncExchangeRates(): Promise<{
    updated: number;
    currencies: Array<{ code: string; name: string; exchangeRate: number }>;
  }> {
    return apiClient.post(ENDPOINTS.CURRENCY_SYNC_RATES);
  }

  async getCurrentExchangeRate(code: string): Promise<number | null> {
    const currencies = await this.getCurrencies();
    const currency = currencies.find(
      (c: Currency) => c.code.toUpperCase() === code.toUpperCase() && c.isActive
    );
    return currency?.exchangeRate ?? null;
  }

  // ============ LOBs ============
  async getLOBs(): Promise<LOB[]> {
    return apiClient.get<LOB[]>(ENDPOINTS.LOBS);
  }

  async getLOB(id: string): Promise<LOB> {
    return apiClient.get<LOB>(`${ENDPOINTS.LOBS}/${id}`);
  }

  async createLOB(data: CreateLOBData): Promise<LOB> {
    return apiClient.post<LOB>(ENDPOINTS.LOBS, data);
  }

  async updateLOB(id: string, data: UpdateLOBData): Promise<LOB> {
    return apiClient.patch<LOB>(`${ENDPOINTS.LOBS}/${id}`, data);
  }

  async deleteLOB(id: string, options?: { cascade?: boolean }): Promise<void> {
    const qs = options?.cascade ? '?cascade=true' : '';
    return apiClient.delete<void>(`${ENDPOINTS.LOBS}/${id}${qs}`);
  }

  // ============ Tax Configs ============
  async getTaxConfigs(): Promise<TaxConfig[]> {
    return apiClient.get<TaxConfig[]>(ENDPOINTS.TAX_CONFIGS);
  }

  async getTaxConfig(id: string): Promise<TaxConfig> {
    return apiClient.get<TaxConfig>(`${ENDPOINTS.TAX_CONFIGS}/${id}`);
  }

  async createTaxConfig(data: CreateTaxConfigData): Promise<TaxConfig> {
    return apiClient.post<TaxConfig>(ENDPOINTS.TAX_CONFIGS, data);
  }

  async updateTaxConfig(id: string, data: UpdateTaxConfigData): Promise<TaxConfig> {
    return apiClient.patch<TaxConfig>(`${ENDPOINTS.TAX_CONFIGS}/${id}`, data);
  }

  async deleteTaxConfig(id: string): Promise<void> {
    return apiClient.delete<void>(`${ENDPOINTS.TAX_CONFIGS}/${id}`);
  }

  // ============ Countries ============
  async getCountries(): Promise<Country[]> {
    return apiClient.get<Country[]>(ENDPOINTS.COUNTRIES);
  }

  async getCountry(id: string): Promise<Country> {
    return apiClient.get<Country>(ENDPOINTS.COUNTRY_BY_ID(id));
  }

  async createCountry(data: CreateCountryData): Promise<Country> {
    return apiClient.post<Country>(ENDPOINTS.COUNTRIES, data);
  }

  async updateCountry(id: string, data: UpdateCountryData): Promise<Country> {
    return apiClient.put<Country>(ENDPOINTS.COUNTRY_BY_ID(id), data);
  }

  async deleteCountry(id: string): Promise<void> {
    return apiClient.delete<void>(ENDPOINTS.COUNTRY_BY_ID(id));
  }

  // ============ Rate Cards ============
  async getRateCards(): Promise<RateCard[]> {
    return apiClient.get<RateCard[]>(ENDPOINTS.RATE_CARDS);
  }

  async getRateCard(id: string): Promise<RateCard> {
    return apiClient.get<RateCard>(`${ENDPOINTS.RATE_CARDS}/${id}`);
  }

  async createRateCard(data: CreateRateCardData): Promise<RateCard> {
    return apiClient.post<RateCard>(ENDPOINTS.RATE_CARDS, data);
  }

  async updateRateCard(id: string, data: UpdateRateCardData): Promise<RateCard> {
    return apiClient.patch<RateCard>(`${ENDPOINTS.RATE_CARDS}/${id}`, data);
  }

  async deleteRateCard(id: string): Promise<void> {
    return apiClient.delete<void>(`${ENDPOINTS.RATE_CARDS}/${id}`);
  }

  // ============ Quote Types ============
  async getQuoteTypes(): Promise<QuoteType[]> {
    return apiClient.get<QuoteType[]>(ENDPOINTS.QUOTE_TYPES);
  }

  async createQuoteType(data: CreateQuoteTypeData): Promise<QuoteType> {
    return apiClient.post<QuoteType>(ENDPOINTS.QUOTE_TYPES, data);
  }

  async updateQuoteType(id: string, data: UpdateQuoteTypeData): Promise<QuoteType> {
    return apiClient.put<QuoteType>(ENDPOINTS.QUOTE_TYPE_BY_ID(id), data);
  }

  async deleteQuoteType(id: string): Promise<void> {
    return apiClient.delete<void>(ENDPOINTS.QUOTE_TYPE_BY_ID(id));
  }

  // ============ CSV Imports ============
  async importCurrencies(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImportResult>(ENDPOINTS.CURRENCIES_IMPORT, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async importLobs(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImportResult>(ENDPOINTS.LOBS_IMPORT, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async importTaxes(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImportResult>(ENDPOINTS.TAX_CONFIGS_IMPORT, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async importCountries(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImportResult>(ENDPOINTS.COUNTRIES_IMPORT, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async importMaterials(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImportResult>(ENDPOINTS.MATERIALS_IMPORT, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // Helper: el endpoint devuelve `{success, data: {created, updated, errors}}`.
  // Lo desempaquetamos acá para que cada importX devuelva directamente
  // un ImportResult, igual que los imports legacy.
  private async _importViaFormData(url: string, file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    const res: any = await apiClient.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res?.data ?? res;
  }

  async importExchangeRates(file: File): Promise<ImportResult> {
    return this._importViaFormData('/api/master-data/exchange-rates/import', file);
  }

  async importQuoteTypes(file: File): Promise<ImportResult> {
    return this._importViaFormData('/api/quote-types/import', file);
  }

  async importProfitCenters(file: File): Promise<ImportResult> {
    return this._importViaFormData('/api/profit-centers/import', file);
  }

  async importIncoterms(file: File): Promise<ImportResult> {
    return this._importViaFormData('/api/incoterm-configs/import', file);
  }

  async importFreightProviders(file: File): Promise<ImportResult> {
    return this._importViaFormData('/api/freight-providers/import', file);
  }

  async importFreightServices(file: File): Promise<ImportResult> {
    return this._importViaFormData('/api/freight-services/import', file);
  }

  async importFreightLegs(file: File): Promise<ImportResult> {
    return this._importViaFormData('/api/freight-legs/import', file);
  }

  async importFreightLocations(file: File): Promise<ImportResult> {
    return this._importViaFormData('/api/freight-locations/import', file);
  }
  async getFreightLocations(): Promise<string[]> {
    return apiClient.get<string[]>('/api/master-data/freight-locations');
  }
}

export const masterDataService = new MasterDataService();
export default masterDataService;
